import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Modal, Space, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router";

import type {
    FindContractSpecialsItem,
    SpecialZoneContractPagingParams,
    SpecialZoneContractPagingRow
} from "@zhihaoscm/entity";
import { ZONE_CONTRACT_GOODS, ZONE_CONTRACT_SPECIAL_ZONE, ZONE_CONTRACT_STATE } from "@zhihaoscm/meta";
import { formatMoney } from "@/utils/money";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { StatusTag } from "@/views/components/StatusTag";
import { showRequestError } from "@/utils/requestErrorMessage";
import AssignDialog, { type assignSubmitData } from "@/views/components/AssignDialog/AssignDialog";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import type { ZoneContractPageResult, ZoneContractReservationResult } from "./zoneContractReactTypes";
import { zoneContractApi } from "./zoneContractReactApi";
import { displayZoneContractCustomerText } from "./zoneContractCustomerLabel";
import { mapContractSpecialsToOfficers } from "./zoneContractAssign";

const CANCELLED_RESERVATION_STATE = 5;
const MAX_EXPORT_ROWS = 10000;
const RESERVATION_PAGE_SIZE = 100;

export interface ZoneContractPageProps {
    loadContracts?: (params: SpecialZoneContractPagingParams) => Promise<ZoneContractPageResult>;
    loadSummary?: (
        params: Omit<SpecialZoneContractPagingParams, "page" | "size">
    ) => Promise<SpecialZoneContractPagingRow>;
    loadCount?: () => Promise<{ effectiveCount: number; cancelCount: number }>;
    loadReservations?: (params: {
        contractId: number;
        page: number;
        size: number;
    }) => Promise<ZoneContractReservationResult>;
    permissions?: string[];
}

const loadContractsDefault = (params: SpecialZoneContractPagingParams) => zoneContractApi.paging(params);
const loadReservationsDefault = (params: { contractId: number; page: number; size: number }) =>
    zoneContractApi.reservations(params);

function customerLabel(row: SpecialZoneContractPagingRow): string {
    const customer = row.businessCustomer;
    // 完整接口响应沿用 Vue 的企业主体展示规则；稀疏夹具/历史响应保留实名兜底。
    if (customer?.institutionName || customer?.applyState != null) {
        return displayZoneContractCustomerText(customer);
    }
    return (
        [customer?.realName || customer?.institutionName || customer?.nickName, customer?.mobile, customer?.code]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

function formatQuantity(value: unknown): string {
    const number = Number(value);
    return value == null || value === "" || !Number.isFinite(number)
        ? "--"
        : number.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

function formatDate(value: unknown, withTime = false): string {
    if (value == null || value === "") return "--";
    const raw = String(value);
    return withTime ? raw.slice(0, 16).replace("T", " ") : raw.slice(0, 10);
}

function contractStateLabel(value: unknown): string {
    const code = Number(value);
    return Number.isFinite(code) ? ZONE_CONTRACT_STATE.getIntroByCode(code) || "--" : "--";
}

/** 为列表行补充旧 Vue 的物料规格与合同量组合文案，便于快速核对合同货量。 */
function contractGoodsSummary(row: SpecialZoneContractPagingRow): string {
    return (row.specialZoneContractGoods || [])
        .map(goods => {
            const name = goods.goodsName || ZONE_CONTRACT_GOODS.getIntroByCode(goods.goodsCode) || "--";
            return `${name} ${formatQuantity(goods.totalTon)}吨`;
        })
        .join("、");
}

/** 合同列表：对应 Vue ProTable，筛选和操作参数沿用 Vue 端字段。 */
export function ZoneContractPage({
    loadContracts = loadContractsDefault,
    loadSummary,
    loadCount = zoneContractApi.count,
    loadReservations = loadReservationsDefault,
    permissions
}: ZoneContractPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_ZONE_CONTRACT_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_ZONE_CONTRACT_DEAL");
    const navigate = useNavigate();
    const [tab, setTab] = useState<number>(ZONE_CONTRACT_STATE.EFFECTIVE.code);
    const [query, setQuery] = useState({
        page: 1,
        // 对齐旧 Vue ProTable/useTable 的默认分页大小。
        size: 10,
        state: ZONE_CONTRACT_STATE.EFFECTIVE.code
    } as SpecialZoneContractPagingParams);
    const [draft, setDraft] = useState({ contractCode: "", customerKey: "", handKey: "" });
    const [contractDateRange, setContractDateRange] = useState<[string | null, string | null]>([null, null]);
    const [createdDateRange, setCreatedDateRange] = useState<[string | null, string | null]>([null, null]);
    const [rows, setRows] = useState<SpecialZoneContractPagingRow[]>([]);
    const [total, setTotal] = useState(0);
    const [summaryRow, setSummaryRow] = useState<SpecialZoneContractPagingRow>();
    const [stats, setStats] = useState({ effective: 0, cancelled: 0 });
    const [error, setError] = useState<string>();
    const [assignTarget, setAssignTarget] = useState<SpecialZoneContractPagingRow>();
    const [cancelTarget, setCancelTarget] = useState<SpecialZoneContractPagingRow>();
    const [officers, setOfficers] = useState<FindContractSpecialsItem[]>([]);
    const [selectedOfficer, setSelectedOfficer] = useState<number>();
    const assignOfficers = useMemo(() => mapContractSpecialsToOfficers(officers), [officers]);
    const summaryLoader = loadSummary || (loadContracts === loadContractsDefault ? zoneContractApi.summary : undefined);

    /** 直接编辑日期输入框时同步筛选草稿，避免失焦后仍沿用旧的日期查询条件。 */
    function syncDateFilterInputs(event: FormEvent<HTMLElement>, kind: "contract" | "created"): void {
        const input = event.target as HTMLInputElement;
        if (input.tagName !== "INPUT") return;
        const values = Array.from(
            input
                .closest(".zone-contract-pick-date-filter, .zone-contract-created-date-filter")
                ?.querySelectorAll("input") || []
        ).map(item => item.value || null) as [string | null, string | null];
        if (values.length !== 2) return;
        if (kind === "contract") setContractDateRange(values);
        else setCreatedDateRange(values);
    }

    /** 恢复旧 Vue 查询表单的重置语义，并重新请求当前页签的第一页。 */
    function resetFilters(): void {
        setDraft({ contractCode: "", customerKey: "", handKey: "" });
        setContractDateRange([null, null]);
        setCreatedDateRange([null, null]);
        setQuery({ page: 1, size: query.size, state: tab });
    }

    useEffect(() => {
        let disposed = false;
        const { page: _page, size: _size, ...summaryParams } = query;
        void _page;
        void _size;
        void Promise.all([
            loadContracts(query),
            summaryLoader ? summaryLoader(summaryParams).catch(() => undefined) : Promise.resolve(undefined)
        ])
            .then(([result, summary]) => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setSummaryRow(summary);
                setError(undefined);
            })
            .catch(() => {
                if (!disposed) setError("专区合同加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadContracts, query, summaryLoader]);

    /** 旧 Vue 表尾按物料规格展示全量合计，接口失败时对应单元格留空。 */
    const summaryMethod = useCallback(
        ({ columns }: { columns: Array<{ prop?: string; property?: string }> }) => {
            const renderSummaryNumber = (value: string) => (
                <span className="zone-contract-number-cell">{value}</span>
            );
            return columns.map((column, index) => {
                if (index === 0) return "合计：";
                const prop = column.prop || column.property || "";
                const match = /^goods-(\d+)-(totalTon|completedTon|settlementTon|settlementAmount)$/.exec(prop);
                if (!match || !summaryRow) return "";
                const goods = summaryRow.specialZoneContractGoods?.find(
                    item => Number(item.goodsCode) === Number(match[1])
                );
                if (!goods) return "";
                const value =
                    match[2] === "settlementAmount"
                        ? formatMoney(goods.settlementAmount)
                        : formatQuantity(goods[match[2] as "totalTon" | "completedTon" | "settlementTon"]);
                return renderSummaryNumber(value);
            });
        },
        [summaryRow]
    );

    useEffect(() => {
        let disposed = false;
        void loadCount()
            .then(result => {
                if (!disposed) setStats({ effective: result.effectiveCount || 0, cancelled: result.cancelCount || 0 });
            })
            .catch(() => undefined);
        return () => {
            disposed = true;
        };
    }, [loadCount]);

    // 将旧 Vue ProTable 的合同列转换为 zhihao-ui-react 配置，保留权限和操作回调。
    const columns = useMemo<TableColumnProps<SpecialZoneContractPagingRow>[]>(
        () => [
            {
                label: "合同编号",
                fixed: "left",
                minWidth: 204,
                prop: "contractCode",
                render: ({ row }) => (
                    <span className="zone-contract-code-cell">
                        <Button
                            type="link"
                            className="zone-contract-code-link"
                            title="双击复制合同编号"
                            onDoubleClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                const code = row.specialZoneContract?.contractCode;
                                if (code) void navigator.clipboard?.writeText(code);
                            }}
                            onClick={() =>
                                navigate(`/tradeManage/zoneContractDetail?id=${row.specialZoneContract?.id}`)
                            }
                        >
                            {row.specialZoneContract?.contractCode || "--"}
                        </Button>
                        <span className="sr-only zone-contract-code-summary">{contractGoodsSummary(row)}</span>
                    </span>
                )
            },
            {
                label: "专区名称",
                minWidth: 176,
                prop: "zone",
                render: ({ row }) =>
                    ZONE_CONTRACT_SPECIAL_ZONE.getIntroByCode(row.specialZoneContract?.specialZoneId) ||
                    String(row.specialZoneContract?.specialZoneId ?? "--")
            },
            { label: "采购用户", width: 350, minWidth: 350, prop: "customer", render: ({ row }) => customerLabel(row) },
            ...ZONE_CONTRACT_GOODS.list.map(item => ({
                label: item.intro,
                prop: `goods-${item.code}`,
                children: [
                    {
                        label: "合同量（吨）",
                        align: "right" as const,
                        headerAlign: "center" as const,
                        minWidth: 190,
                        prop: `goods-${item.code}-totalTon`,
                        render: ({ row }: { row: SpecialZoneContractPagingRow }) => {
                            const goods = row.specialZoneContractGoods?.find(
                                value => Number(value.goodsCode) === Number(item.code)
                            );
                            return <span className="zone-contract-number-cell">{formatQuantity(goods?.totalTon)}</span>;
                        }
                    },
                    {
                        label: "完成量（吨）",
                        align: "right" as const,
                        headerAlign: "center" as const,
                        minWidth: 190,
                        prop: `goods-${item.code}-completedTon`,
                        render: ({ row }: { row: SpecialZoneContractPagingRow }) => {
                            const goods = row.specialZoneContractGoods?.find(
                                value => Number(value.goodsCode) === Number(item.code)
                            );
                            return (
                                <span className="zone-contract-number-cell">{formatQuantity(goods?.completedTon)}</span>
                            );
                        }
                    },
                    {
                        label: "结算吨数（吨）",
                        align: "right" as const,
                        headerAlign: "center" as const,
                        minWidth: 190,
                        prop: `goods-${item.code}-settlementTon`,
                        render: ({ row }: { row: SpecialZoneContractPagingRow }) => {
                            const goods = row.specialZoneContractGoods?.find(
                                value => Number(value.goodsCode) === Number(item.code)
                            );
                            return (
                                <span className="zone-contract-number-cell">
                                    {formatQuantity(goods?.settlementTon)}
                                </span>
                            );
                        }
                    },
                    {
                        label: "结算金额（元）",
                        align: "right" as const,
                        headerAlign: "center" as const,
                        minWidth: 190,
                        prop: `goods-${item.code}-settlementAmount`,
                        render: ({ row }: { row: SpecialZoneContractPagingRow }) => {
                            const goods = row.specialZoneContractGoods?.find(
                                value => Number(value.goodsCode) === Number(item.code)
                            );
                            return (
                                <span className="zone-contract-number-cell">
                                    {formatMoney(goods?.settlementAmount)}
                                </span>
                            );
                        }
                    }
                ]
            })),
            {
                label: "可预约金额（元）",
                align: "right",
                headerAlign: "center",
                minWidth: 204,
                prop: "availableRemainingAmount",
                render: ({ row }) => (
                    <span className="zone-contract-number-cell">
                        {formatMoney(
                            Number(row.specialZoneAccount?.balanceAmount || 0) -
                                Number(row.specialZoneAccount?.occAmount || 0)
                        )}
                    </span>
                )
            },
            {
                label: "合同专员",
                width: 148,
                prop: "handlerName",
                render: ({ row }) => row.specialZoneContract?.handlerName || "--"
            },
            {
                label: "合同日期",
                width: 350,
                prop: "date",
                render: ({ row }) =>
                    `${formatDate(row.specialZoneContract?.pickStartDate)} ～ ${formatDate(row.specialZoneContract?.pickEndDate)}`
            },
            {
                label: "签订日期",
                width: 138,
                prop: "signDate",
                render: ({ row }) => formatDate(row.specialZoneContract?.signDate)
            },
            {
                label: "创建时间",
                width: 188,
                prop: "createdTime",
                render: ({ row }) => formatDate(row.specialZoneContract?.createdTime, true)
            },
            {
                label: "状态",
                width: 120,
                prop: "state",
                render: ({ row }) => (
                    <StatusTag
                        appearance="tag"
                        tone={
                            Number(row.specialZoneContract?.state) === ZONE_CONTRACT_STATE.CANCELLED.code
                                ? "info"
                                : "success"
                        }
                        className={`zone-contract-status ${
                            Number(row.specialZoneContract?.state) === ZONE_CONTRACT_STATE.CANCELLED.code
                                ? "is-cancelled"
                                : "is-effective"
                        }`}
                    >
                        {contractStateLabel(row.specialZoneContract?.state)}
                    </StatusTag>
                )
            },
            {
                label: "操作",
                fixed: "right",
                // 旧 Vue 表格在当前权限下的三项操作固定为 205px，避免长采购用户文本把固定列推移。
                width: 205,
                className: "admin-table-operation-column compact",
                labelClassName: "admin-table-operation-column compact",
                prop: "operation",
                render: ({ row }) => {
                    const id = row.specialZoneContract?.id;
                    if (tab !== ZONE_CONTRACT_STATE.EFFECTIVE.code || id == null) return null;
                    return (
                        <Space>
                            {canManage ? (
                                <Button
                                    aria-label={`指派合同${row.specialZoneContract.contractCode}`}
                                    type="link"
                                    onClick={() => void openAssign(row)}
                                >
                                    {" "}
                                    {row.specialZoneContract.handlerId ? "变更指派" : "指派"}
                                </Button>
                            ) : null}
                            {canDeal ? (
                                <Button type="link" onClick={() => navigate(`/tradeManage/zoneContract/edit/${id}`)}>
                                    编辑
                                </Button>
                            ) : null}
                            {canDeal ? (
                                <Button
                                    aria-label={`取消合同${row.specialZoneContract.contractCode}`}
                                    danger
                                    type="link"
                                    onClick={() => void cancel(row)}
                                >
                                    取消
                                </Button>
                            ) : null}
                        </Space>
                    );
                }
            }
        ],
        [canDeal, canManage, navigate, tab]
    );
    // 保持传给 zhihao Table 的列引用稳定，避免其布局测量 effect 因每次渲染拿到新数组而循环更新。
    const tableColumns = useMemo(() => normalizeOperationColumns(columns, rows), [columns, rows]);

    async function openAssign(row: SpecialZoneContractPagingRow) {
        try {
            setOfficers(await zoneContractApi.specials());
            setSelectedOfficer(row.specialZoneContract?.handlerId || undefined);
            setAssignTarget(row);
        } catch (error) {
            showRequestError(error, "加载合同专员列表失败");
        }
    }

    async function cancel(row: SpecialZoneContractPagingRow) {
        const id = row.specialZoneContract?.id;
        if (id == null) return;
        try {
            let page = 1;
            while (true) {
                const reservation = await loadReservations({ contractId: id, page, size: RESERVATION_PAGE_SIZE });
                const reservationRows = reservation.list || [];
                const blocked = reservationRows.some(
                    item =>
                        Number(item.specialZoneReservationOrder?.state ?? item.state) !== CANCELLED_RESERVATION_STATE
                );
                if (blocked) {
                    message.warning("合同下已有预约单，无法取消，请先取消预约单！");
                    return;
                }
                if (reservationRows.length < RESERVATION_PAGE_SIZE) break;
                page += 1;
            }
            setCancelTarget(row);
        } catch (error) {
            showRequestError(error, "校验失败，请稍后重试");
        }
    }

    async function submitAssign({ officerId }: assignSubmitData) {
        if (!assignTarget || officerId == null) return;
        await zoneContractApi.assign(assignTarget.specialZoneContract.id, Number(officerId));
        setAssignTarget(undefined);
        setSelectedOfficer(undefined);
        setQuery(current => ({ ...current }));
        void loadCount().catch(() => undefined);
        message.success("指派成功");
    }

    async function exportList() {
        try {
            const result = await loadContracts({ ...query, page: 1, size: 1 });
            if ((result.total || 0) > MAX_EXPORT_ROWS) {
                message.error(`导出失败：当前查询结果超过 ${MAX_EXPORT_ROWS} 条，请缩小筛选条件后重试`);
                return;
            }
            const { page: _page, size: _size, ...exportQuery } = query;
            void _page;
            void _size;
            const now = new Date();
            const fileSuffix = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
            await zoneContractApi.export(exportQuery, `专区合同${fileSuffix}.xlsx`);
            message.success("导出成功");
        } catch (error) {
            showRequestError(error, "导出失败");
        }
    }

    return (
        <section aria-label="zone-contract-page" className="admin-trade-list-page">
            <Card>
                <ZhTable
                    columns={tableColumns}
                    data={rows}
                    // Vue ProTable 将列表主体限制在视口内，分页始终保留在首屏；宽表仍由表格自身横向滚动。
                    maxHeight="calc(100vh - 430px)"
                    pagination={false}
                    rowKey={row => String(row.specialZoneContract?.id)}
                    showSummary
                    sumText="合计"
                    summaryMethod={summaryMethod}
                    // Vue 旧版使用固定列宽；固定布局避免采购用户长文本把后续宽表列整体撑偏。
                    tableLayout="fixed"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4}>专区合同</Typography.Title>

                            <Space className="admin-trade-stat-cards" size={0}>
                                <button
                                    aria-selected={tab === ZONE_CONTRACT_STATE.EFFECTIVE.code}
                                    aria-label="有效合同"
                                    className={`admin-trade-stat-card is-selectable ${tab === ZONE_CONTRACT_STATE.EFFECTIVE.code ? "is-active" : ""}`}
                                    type="button"
                                    onClick={() => {
                                        setTab(1);
                                        setQuery({ ...query, page: 1, state: 1 });
                                    }}
                                >
                                    <span className="admin-trade-stat-card__label">有效合同</span>
                                    <span className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.effective}
                                    </span>
                                </button>
                                <button
                                    aria-selected={tab === ZONE_CONTRACT_STATE.CANCELLED.code}
                                    aria-label="已取消合同"
                                    className={`admin-trade-stat-card is-selectable is-cancelled ${tab === ZONE_CONTRACT_STATE.CANCELLED.code ? "is-active" : ""}`}
                                    type="button"
                                    onClick={() => {
                                        setTab(2);
                                        setQuery({ ...query, page: 1, state: 2 });
                                    }}
                                >
                                    <span className="admin-trade-stat-card__label">已取消合同</span>
                                    <span className="admin-trade-stat-card__value admin-trade-stat-card__value--zone">
                                        {stats.cancelled}
                                    </span>
                                </button>
                            </Space>

                            {error ? <Alert message={error} type="error" showIcon /> : null}

                            <div className="admin-table-filter-form mb-4">
                                <Space className="zone-contract-filter-row zone-contract-filter-row--top">
                                    {canManage ? (
                                        <>
                                            <Button
                                                aria-label="新增专区合同"
                                                type="primary"
                                                onClick={() => navigate("/tradeManage/zoneContract/add")}
                                            >
                                                新增
                                            </Button>
                                            <Button aria-label="导出专区合同" onClick={() => void exportList()}>
                                                导出
                                            </Button>
                                        </>
                                    ) : null}
                                    <Input
                                        aria-label="合同编号"
                                        className="zone-contract-code-filter"
                                        placeholder="请输入合同编号搜索"
                                        allowClear
                                        value={draft.contractCode}
                                        onChange={event => setDraft({ ...draft, contractCode: event.target.value })}
                                    />
                                    <Input
                                        aria-label="客户关键词"
                                        className="zone-contract-customer-filter"
                                        placeholder="请输入用户ID/个人或机构实名/手机号搜索"
                                        allowClear
                                        value={draft.customerKey}
                                        onChange={event => setDraft({ ...draft, customerKey: event.target.value })}
                                    />
                                    <Input
                                        aria-label="处理专员关键词"
                                        className="zone-contract-handler-filter"
                                        placeholder="请输入合同专员姓名搜索"
                                        allowClear
                                        value={draft.handKey}
                                        onChange={event => setDraft({ ...draft, handKey: event.target.value })}
                                    />
                                    <ZhDateRangePicker
                                        aria-label="合同日期"
                                        className="zone-contract-pick-date-filter"
                                        clearable
                                        endPlaceholder="合同结束日期"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="至"
                                        startPlaceholder="合同开始日期"
                                        type="daterange"
                                        value={contractDateRange}
                                        valueFormat="YYYY-MM-DD"
                                        onInput={event => syncDateFilterInputs(event, "contract")}
                                        onChange={(_value, formattedValue) =>
                                            setContractDateRange(
                                                formattedValue
                                                    ? [formattedValue[0] || null, formattedValue[1] || null]
                                                    : [null, null]
                                            )
                                        }
                                    />
                                    <ZhDateRangePicker
                                        aria-label="创建时间"
                                        className="zone-contract-created-date-filter"
                                        clearable
                                        endPlaceholder="创建结束日期"
                                        format="YYYY-MM-DD"
                                        rangeSeparator="至"
                                        startPlaceholder="创建开始日期"
                                        type="daterange"
                                        value={createdDateRange}
                                        valueFormat="YYYY-MM-DD"
                                        onInput={event => syncDateFilterInputs(event, "created")}
                                        onChange={(_value, formattedValue) =>
                                            setCreatedDateRange(
                                                formattedValue
                                                    ? [formattedValue[0] || null, formattedValue[1] || null]
                                                    : [null, null]
                                            )
                                        }
                                    />
                                    <Button
                                        aria-label="查询专区合同"
                                        className="zone-contract-search-filter"
                                        icon={<TableSearchIcon name="search" />}
                                        type="primary"
                                        onClick={() => {
                                            const nextQuery = { ...query } as SpecialZoneContractPagingParams;
                                            for (const key of [
                                                "contractCode",
                                                "customerKey",
                                                "handKey",
                                                "pickStartDate",
                                                "pickEndDate",
                                                "createStartDate",
                                                "createEndDate"
                                            ] as const) {
                                                delete nextQuery[key];
                                            }
                                            Object.assign(nextQuery, {
                                                ...(draft.contractCode ? { contractCode: draft.contractCode } : {}),
                                                ...(draft.customerKey ? { customerKey: draft.customerKey } : {}),
                                                ...(draft.handKey ? { handKey: draft.handKey } : {}),
                                                ...(contractDateRange[0]
                                                    ? { pickStartDate: contractDateRange[0] }
                                                    : {}),
                                                ...(contractDateRange[1] ? { pickEndDate: contractDateRange[1] } : {}),
                                                ...(createdDateRange[0]
                                                    ? { createStartDate: createdDateRange[0] }
                                                    : {}),
                                                ...(createdDateRange[1] ? { createEndDate: createdDateRange[1] } : {}),
                                                page: 1
                                            });
                                            setQuery(nextQuery);
                                        }}
                                    >
                                        搜索
                                    </Button>
                                    <Button
                                        aria-label="重置专区合同筛选"
                                        className="zone-contract-reset-filter"
                                        icon={<TableSearchIcon name="reset" />}
                                        onClick={resetFilters}
                                    >
                                        重置
                                    </Button>
                                </Space>
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                className="mt-4 self-end"
                                current={query.page}
                                pageSize={query.size}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                total={total}
                                onChange={(page, size) =>
                                    setQuery(current => ({ ...current, page: size !== current.size ? 1 : page, size }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <AssignDialog
                open={Boolean(assignTarget)}
                title="指派专员"
                info={assignTarget}
                officers={assignOfficers}
                tips="跟进的合同数量"
                countFiled="orderCount"
                curOfficerId={selectedOfficer}
                onClose={() => {
                    setAssignTarget(undefined);
                    setSelectedOfficer(undefined);
                }}
                onCurOfficerIdChange={setSelectedOfficer}
                onSubmit={submitAssign}
            />
            <Modal
                open={Boolean(cancelTarget)}
                title="取消合同"
                onCancel={() => setCancelTarget(undefined)}
                onOk={async () => {
                    const id = cancelTarget?.specialZoneContract?.id;
                    if (id == null) return;
                    try {
                        await zoneContractApi.cancel(id);
                        setCancelTarget(undefined);
                        setQuery(current => ({ ...current }));
                        void loadCount().catch(() => undefined);
                    } catch (error) {
                        showRequestError(error, "取消合同失败");
                    }
                }}
            >
                确认取消合同吗？
            </Modal>
        </section>
    );
}

export default ZoneContractPage;
